import styled, { css } from 'styled-components';
import type { ThemeProps } from 'styled-components';
import type { AllColorType, ThemeType } from '../../util/types';
import { getPolishedColors } from '../../util/styled';
import { Z_INDEXES } from '../../util/vars';

const getColors = (props: ThemeProps<ThemeType> & { $color: AllColorType; $showTbodyBorder: boolean }) => {
  const colors = getPolishedColors(props, 'solid');
  return css`
    border-color: ${colors.main.color} !important;
    thead,
    tbody {
      th,
      td {
        border-right-color: ${colors.main.color};
      }
    }
    tbody {      
      ${props.$showTbodyBorder && `box-shadow: inset 0 0 0 1px ${props.theme.colors.table_border};`};
    }
    tbody tr {
      &:first-child {      
        border-top-color: ${props.theme.colors.muted};
      }
      border-top-color: ${colors.main.color};
    }
    table [data-row='row-parent'][data-odd='true'] {
      background: ${colors.most_light.color};
    }
    table [data-row='row-parent'][data-odd='false'] {
      background: #ffffff;
    }
    table tr[data-row='row-child'] {
      background: rgba(255, 255, 255, .25);
    }
    table [data-row='row-parent'][data-new-entry='true'] {
      background: ${props.theme.colors.positive_light};
    }
  `;
};


export const TableStyled = styled('div')<{ $color: AllColorType; $showTbodyBorder: boolean; $stickyTop?: number }>`
  border: 0px solid transparent;

  && table {
    width: 100%;
    cursor: default;
    vertical-align: middle;
    border-collapse: collapse;
    text-align: left;
    thead,
    tbody {
      th,
      td {        
        height: ${(props) => props.theme.sizes.md};
        padding: 5px 10px;
        border-right: 0px solid transparent;
        &:last-child {
          border-right: none;
        }
      }      
    }
    thead th {
      font-weight: normal;
      font-style: italic;
      font-size: ${(props) => props.theme.fonts.sm};
      &[data-id='col_selection'] {
        &,
        & > div,
        & > div > div {
          width: 0 !important;
          height: 0 !important;          
        }
      }
    }
    tbody tr {
      border-top: 1px solid transparent;
      &[data-row='row-parent'] td[data-column='cell_name'], [data-sorted='true'] {
        font-weight: bold;
      }
      &[data-row='row-child'] ul {
        padding: 10px 25px;
        li {
          margin-bottom: 16px;
        }
        li:last-child {
          margin-bottom: 0;
        }
      }
      &[data-disabled='true'] {
        opacity: 0.5;
      }
    }

    &.sticky-table {
      display: block;
      border-collapse: separate;
      border-spacing: 0;
      thead,
      tfoot {
        position: relative;
        z-index: ${Z_INDEXES.level_3 + 2};
        width: fit-content;
        background: #ffffff;
        tr {
          background: inherit;
        }
      }

      tbody {
        z-index: 0;
      }

      th {
        position: sticky;
        top: ${(props) => props.$stickyTop ?? 0}px;
        z-index: ${Z_INDEXES.level_3} !important;
        background: #ffffff;
        box-shadow: 0px 1px 0px ${(props) => props.theme.colors.muted};
      }

      th[data-sticky-td] {
        z-index: ${Z_INDEXES.level_3 + 3} !important;
      }

      [data-sticky-td] {
        z-index: ${Z_INDEXES.level_3 + 1} !important;
        position: sticky;
        background: inherit;
      }
    }

    &.hidden {
      display: none;
    }

  }

  ${(props) => getColors(props)}
`;
